import { trpc } from "../trpc";
import { useMemo } from "react";
import {
  transformCanvasConnections,
  transformChatbotConnections,
  transformInterfaceConnections,
  transformSubscriptionConnections,
  transformTableViewConnections,
  transformZapConnections,
} from "./tableConnectedAssetTransforms";

export function useTableConnectedAssets(tableId?: string) {
  const {
    data: canvases,
    isLoading: isCanvasesLoading,
    isFetching: isCanvasesFetching,
  } = trpc.table.connectedCanvases.useQuery(
    { tableId: tableId || "" },
    { enabled: !!tableId },
  );

  const {
    data: interfaces,
    isLoading: isInterfacesLoading,
    isFetching: isInterfacesFetching,
  } = trpc.table.connectedInterfaces.useQuery(
    { tableId: tableId || "" },
    { enabled: !!tableId },
  );

  const {
    data: chatbots,
    isLoading: isChatbotsLoading,
    isFetching: isChatbotsFetching,
  } = trpc.table.connectedChatbots.useQuery(
    { tableId: tableId || "" },
    { enabled: !!tableId },
  );

  const {
    data: zaps,
    isLoading: isZapsLoading,
    isFetching: isZapsFetching,
  } = trpc.table.connectedZaps.useQuery(
    { tableId: tableId || "" },
    { enabled: !!tableId },
  );

  const {
    data: subscriptions,
    isLoading: isSubscriptionsLoading,
    isFetching: isSubscriptionsFetching,
  } = trpc.table.connectedSubscriptions.useQuery(
    { tableId: tableId || "" },
    { enabled: !!tableId },
  );

  const {
    data: tableViews,
    isLoading: isTableViewsLoading,
    isFetching: isTableViewsFetching,
  } = trpc.table.connectedViews.useQuery(
    { tableId: tableId || "" },
    { enabled: !!tableId },
  );

  const transformedCanvases = useMemo(
    () => transformCanvasConnections(canvases?.tableConnectedCanvases),
    [canvases],
  );

  const transformedInterfaces = useMemo(
    () => transformInterfaceConnections(interfaces?.tableConnectedInterfaces),
    [interfaces],
  );

  const transformedChatbots = useMemo(
    () => transformChatbotConnections(chatbots?.tableConnectedChatbots),
    [chatbots],
  );

  const transformedZaps = useMemo(
    () => transformZapConnections(zaps?.tableConnectedZaps),
    [zaps],
  );

  const transformedSubscriptions = useMemo(
    () => transformSubscriptionConnections(subscriptions?.tableSubscriptions),
    [subscriptions],
  );

  const transformedTableViews = useMemo(
    () => transformTableViewConnections(tableViews?.tableViews),
    [tableViews],
  );

  const transformedAssets = useMemo(
    () => [
      ...transformedCanvases,
      ...transformedInterfaces,
      ...transformedChatbots,
      ...transformedZaps,
      ...transformedSubscriptions,
      ...transformedTableViews,
    ],
    [
      transformedCanvases,
      transformedInterfaces,
      transformedChatbots,
      transformedZaps,
      transformedSubscriptions,
      transformedTableViews,
    ],
  );

  const hasNonBlockingConnections =
    transformedCanvases.length > 0 ||
    transformedChatbots.length > 0 ||
    transformedZaps.length > 0 ||
    transformedSubscriptions.length > 0;

  const hasBlockingConnections = transformedInterfaces.length > 0;

  return {
    hasNonBlockingConnections,
    hasBlockingConnections,
    hasSubscriptions: transformedSubscriptions.length > 0,
    hasViews: transformedTableViews.length > 0,
    transformedAssets,
    isLoading:
      isCanvasesLoading ||
      isInterfacesLoading ||
      isChatbotsLoading ||
      isZapsLoading ||
      isSubscriptionsLoading ||
      isTableViewsLoading,
    isFetching:
      isCanvasesFetching ||
      isInterfacesFetching ||
      isChatbotsFetching ||
      isZapsFetching ||
      isSubscriptionsFetching ||
      isTableViewsFetching,
  };
}
